import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Snackbar, Alert, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress, useMediaQuery, useTheme } from '@mui/material';
import {
  getMembres, getAllRelations, addMembre, updateMembre,
  deleteMembre, addRelation, updateRelation, deleteRelation, getMembreById,
} from './api';
import { Membre, Relation } from './types';
import { buildTree } from './utils/buildTree';
import { getFocusFamily } from './utils/getFocusFamily';
import { exportGEDCOM } from './utils/gedcom';
import { parseGedcom, importGedcom, GedcomParseResult } from './utils/gedcomImport';
import { trackPageview } from './utils/pageview';
import { initScrollTracker } from './utils/scrollTracker';
import { initOutboundTracker } from './utils/outboundTracker';
import AppHeader from './components/AppHeader';
import MemberSidebar from './components/MemberSidebar';
import FamilyTree from './components/FamilyTree';
import MemberDrawer from './components/MemberDrawer';
import MemberFormDialog from './components/MemberFormDialog';
import RelationFormDialog from './components/RelationFormDialog';
import ExportTreeDialog from './components/ExportTreeDialog';
import EngineCompareDialog from './components/EngineCompareDialog';
import Dashboard from './components/Dashboard';
import Timeline from './components/Timeline';

type Severity = 'success' | 'error' | 'warning' | 'info';
type View = 'tree' | 'dashboard' | 'timeline';

function App() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [membres, setMembres] = useState<Membre[]>([]);
  const [relations, setRelations] = useState<Relation[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [view, setView] = useState<View>('dashboard');
  const [focusMemberId, setFocusMemberId] = useState<number | null>(null);

  const [memberFormOpen, setMemberFormOpen] = useState(false);
  const [editingMembre, setEditingMembre] = useState<Membre | null>(null);

  const [relationFormOpen, setRelationFormOpen] = useState(false);
  const [relationDefaultSource, setRelationDefaultSource] = useState<number | undefined>();

  // Export d'arbre (modale multi-variantes)
  const [exportOpen, setExportOpen] = useState(false);
  // Comparateur de moteurs de rendu (Topola / family-chart / Balkan)
  const [engineCompareOpen, setEngineCompareOpen] = useState(false);
  const captureCardTreeRef = useRef<(() => Promise<{ dataUrl: string; width: number; height: number } | null>) | null>(null);

  // GEDCOM import state
  const [gedcomPreview, setGedcomPreview] = useState<GedcomParseResult | null>(null);
  const [gedcomImporting, setGedcomImporting] = useState(false);
  const [gedcomProgress, setGedcomProgress] = useState('');

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: Severity }>({
    open: false, message: '', severity: 'success',
  });

  // Données filtrées si focus actif
  const { focusMembers, focusRelations } = useMemo(() => {
    if (!focusMemberId) return { focusMembers: membres, focusRelations: relations };
    const f = getFocusFamily(focusMemberId, membres, relations);
    return { focusMembers: f.membres, focusRelations: f.relations };
  }, [focusMemberId, membres, relations]);

  const treeData = useMemo(
    () => buildTree(focusMembers, focusRelations),
    [focusMembers, focusRelations]
  );

  const selectedMembre = useMemo(
    () => membres.find(m => m.id === selectedId) ?? null,
    [membres, selectedId]
  );

  const showSnackbar = (message: string, severity: Severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const refresh = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([getMembres(), getAllRelations()]);
      setMembres(m);
      setRelations(r);
    } catch {
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Pageview Matomo : un hit par changement de vue (le SPA n'a qu'une URL
  // réelle). Le path virtuel correspond à la vue active. trackPageview demarre
  // aussi le heartbeat 15s et reset les seuils scroll pour la nouvelle vue.
  useEffect(() => {
    trackPageview(`/${view}`);
  }, [view]);

  // Trackers transverses (scroll % global + outbound clicks + downloads).
  // Init une seule fois au mount. Les services filtrent en interne (no-op
  // avec opt-out cookie matomo_ignore).
  useEffect(() => {
    initScrollTracker();
    initOutboundTracker();
  }, []);

  // Close sidebar on mobile when drawer opens
  useEffect(() => {
    if (isMobile && drawerOpen) setSidebarOpen(false);
  }, [isMobile, drawerOpen]);

  // ─── Member handlers ───────────────────────────────────────────────────────

  const handleSelectMember = (id: number) => {
    setSelectedId(id);
    setDrawerOpen(true);
    if (view !== 'tree') setView('tree');
    if (isMobile) setSidebarOpen(false);
  };

  const handleOpenAddMember = () => {
    setEditingMembre(null);
    setMemberFormOpen(true);
  };

  const handleEditMember = async (membre: Membre) => {
    try {
      const full = await getMembreById(membre.id);
      setEditingMembre(full);
    } catch {
      setEditingMembre(membre);
    }
    setMemberFormOpen(true);
  };

  const handleEditMemberById = async (id: number) => {
    const m = membres.find(x => x.id === id);
    if (m) await handleEditMember(m);
  };

  const handleSaveMember = async (data: Omit<Membre, 'id'>) => {
    try {
      if (editingMembre) {
        await updateMembre(editingMembre.id, data);
        showSnackbar('Membre modifié avec succès');
        setMemberFormOpen(false);
        setEditingMembre(null);
        await refresh();
      } else {
        // Détection de doublon
        const nom = (data.nom || '').toLowerCase().trim();
        const prenom = (data.prenom || '').toLowerCase().trim();
        const isDuplicate = membres.some(
          m => m.nom.toLowerCase().trim() === nom && m.prenom.toLowerCase().trim() === prenom
        );
        if (isDuplicate) {
          const confirmed = window.confirm(
            `Un membre nommé "${data.prenom} ${data.nom}" existe déjà. Voulez-vous quand même l'ajouter ?`
          );
          if (!confirmed) return;
        }
        const hasOthers = membres.length >= 1;
        const newMembre = await addMembre(data);
        setMemberFormOpen(false);
        await refresh();
        if (hasOthers) {
          setRelationDefaultSource(newMembre.id);
          setRelationFormOpen(true);
          showSnackbar('Membre ajouté ! Créez maintenant sa relation familiale.', 'info');
        } else {
          showSnackbar('Membre ajouté avec succès');
        }
      }
    } catch {
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDeleteMember = async (id: number) => {
    try {
      await deleteMembre(id);
      showSnackbar('Membre supprimé');
      setDrawerOpen(false);
      setSelectedId(null);
      if (focusMemberId === id) setFocusMemberId(null);
      await refresh();
    } catch {
      showSnackbar('Erreur lors de la suppression', 'error');
    }
  };

  // ─── Relation handlers ─────────────────────────────────────────────────────

  const handleOpenAddRelation = (sourceId?: number) => {
    setRelationDefaultSource(sourceId);
    setRelationFormOpen(true);
  };

  const handleSaveRelation = async (data: {
    membre_source: number; membre_cible: number; type_relation: string;
    date_debut?: string; date_fin?: string;
  }) => {
    try {
      await addRelation(data);
      showSnackbar('Relation créée avec succès');
      setRelationFormOpen(false);
      await refresh();
    } catch {
      showSnackbar('Erreur lors de la création de la relation', 'error');
    }
  };

  const handleUpdateRelation = async (id: number, data: { date_debut?: string; date_fin?: string }) => {
    try {
      await updateRelation(id, data);
      showSnackbar('Dates mises à jour');
      await refresh();
    } catch {
      showSnackbar('Erreur lors de la mise à jour des dates', 'error');
    }
  };

  const handleDeleteRelation = async (id: number) => {
    try {
      await deleteRelation(id);
      showSnackbar('Relation supprimée');
      await refresh();
    } catch {
      showSnackbar('Erreur lors de la suppression de la relation', 'error');
    }
  };

  const handleExportGEDCOM = () => {
    exportGEDCOM(membres, relations);
    showSnackbar('Export GEDCOM téléchargé', 'success');
  };

  // ─── GEDCOM import ────────────────────────────────────────────────────────

  const handleImportGEDCOM = (file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const content = e.target?.result;
        if (typeof content !== 'string') return;
        const parsed = parseGedcom(content);
        setGedcomPreview(parsed);
      } catch {
        showSnackbar('Fichier GEDCOM invalide', 'error');
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleConfirmImport = async () => {
    if (!gedcomPreview) return;
    setGedcomImporting(true);
    setGedcomProgress('Démarrage…');
    try {
      const result = await importGedcom(gedcomPreview, msg => setGedcomProgress(msg));
      setGedcomPreview(null);
      await refresh();
      showSnackbar(`Import réussi : ${result.membres} membres, ${result.relations} relations`, 'success');
    } catch {
      showSnackbar('Erreur lors de l\'import GEDCOM', 'error');
    } finally {
      setGedcomImporting(false);
      setGedcomProgress('');
    }
  };

  // ─── Loading state ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100dvh', flexDirection: 'column', gap: 2, bgcolor: 'background.default' }}>
        <CircularProgress size={40} thickness={4} />
        <Typography color="text.secondary" variant="body2">Chargement de l'arbre familial…</Typography>
      </Box>
    );
  }

  // Mobile drawer width: full screen
  const drawerWidth = isMobile ? '100vw' : 340;

  // ─── Main layout ───────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden' }}>
      <AppHeader
        onAddMember={handleOpenAddMember}
        onToggleSidebar={() => setSidebarOpen(o => !o)}
        memberCount={membres.length}
        view={view}
        onSetView={setView}
        onExportGEDCOM={handleExportGEDCOM}
        onImportGEDCOM={handleImportGEDCOM}
        onOpenExport={() => setExportOpen(true)}
        onOpenEngineCompare={() => setEngineCompareOpen(true)}
      />

      {view === 'dashboard' && <Dashboard membres={membres} relations={relations} />}
      {view === 'timeline' && <Timeline membres={membres} relations={relations} onSelectMember={handleSelectMember} />}

      {/* Tree view — toujours monté pour préserver l'état de zoom */}
      <Box sx={{ display: view === 'tree' ? 'flex' : 'none', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Sidebar overlay on mobile */}
        {isMobile && sidebarOpen && (
          <Box
            sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', zIndex: 200 }}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <Box sx={isMobile ? {
          position: 'absolute', top: 0, bottom: 0, left: 0,
          zIndex: 201, transition: 'transform 0.25s ease',
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        } : {}}>
          <MemberSidebar
            open={isMobile ? true : sidebarOpen}
            membres={membres}
            relations={relations}
            selectedId={selectedId}
            onSelect={id => { handleSelectMember(id); if (isMobile) setSidebarOpen(false); }}
          />
        </Box>

        <FamilyTree
          treeData={treeData}
          membres={membres}
          relations={relations}
          selectedId={selectedId}
          onNodeClick={handleSelectMember}
          onEditMember={handleEditMemberById}
          onAddMember={handleOpenAddMember}
          focusMemberId={focusMemberId}
          onSetFocus={setFocusMemberId}
          active={view === 'tree'}
          onOpenExport={() => setExportOpen(true)}
          onRegisterCapture={fn => { captureCardTreeRef.current = fn; }}
        />

        <Box sx={{
          position: 'absolute', top: 0, right: 0, bottom: 0,
          width: drawerOpen ? drawerWidth : 0,
          overflow: 'hidden', transition: 'width 0.25s ease',
          borderLeft: drawerOpen ? '1px solid' : 'none',
          borderColor: 'divider', bgcolor: 'background.paper', zIndex: 100,
        }}>
          {drawerOpen && selectedMembre && (
            <MemberDrawer
              membre={selectedMembre}
              membres={membres}
              relations={relations}
              onClose={() => setDrawerOpen(false)}
              onEdit={handleEditMember}
              onDelete={handleDeleteMember}
              onAddRelation={handleOpenAddRelation}
              onDeleteRelation={handleDeleteRelation}
              onUpdateRelation={handleUpdateRelation}
              onSelectMember={handleSelectMember}
            />
          )}
        </Box>
      </Box>

      <MemberFormDialog
        open={memberFormOpen}
        membre={editingMembre}
        onClose={() => { setMemberFormOpen(false); setEditingMembre(null); }}
        onSave={handleSaveMember}
      />

      <RelationFormDialog
        open={relationFormOpen}
        membres={membres}
        defaultSourceId={relationDefaultSource}
        onClose={() => setRelationFormOpen(false)}
        onSave={handleSaveRelation}
      />

      <ExportTreeDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        membres={membres}
        relations={relations}
        defaultRootId={selectedId ?? focusMemberId}
        defaultDark={theme.palette.mode === 'dark'}
        captureCardTree={() => captureCardTreeRef.current?.() ?? Promise.resolve(null)}
      />

      <EngineCompareDialog
        open={engineCompareOpen}
        onClose={() => setEngineCompareOpen(false)}
        membres={membres}
        relations={relations}
        defaultRootId={selectedId ?? focusMemberId}
      />

      {/* GEDCOM import confirmation dialog */}
      <Dialog open={!!gedcomPreview || gedcomImporting} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>Import GEDCOM</DialogTitle>
        <DialogContent>
          {gedcomImporting ? (
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" gutterBottom>{gedcomProgress}</Typography>
              <LinearProgress sx={{ borderRadius: 2 }} />
            </Box>
          ) : gedcomPreview && (
            <Typography variant="body2">
              Fichier analysé :<br />
              <strong>{gedcomPreview.membres}</strong> personnes et <strong>{gedcomPreview.familles}</strong> familles seront importées.
              <br /><br />
              Cette opération ajoutera des membres à votre arbre existant.
            </Typography>
          )}
        </DialogContent>
        {!gedcomImporting && (
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => setGedcomPreview(null)} variant="outlined" size="small">Annuler</Button>
            <Button onClick={handleConfirmImport} variant="contained" size="small" disableElevation>
              Importer
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled" sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default App;
